import React, { memo } from 'react';
import { ProposalBlockFormData, ProposalBlockForm } from '../../ProposalForm';
import { R } from '@core/utils/r';
import { SortableHandle, SortableElement } from 'react-sortable-hoc';
import classNames from 'classnames';
import { openBlockSettingsOverlay } from '../BlockSettingsOverlay';
import { CommonService } from '@business/services';
import { isAlwaysVisible, isHiddenInNav } from './navGroups';

export interface SectionItemProps {
  block: ProposalBlockFormData;
  onClick(): void;
  blockForm: ProposalBlockForm;
  onSettings(): void;
  // Navigation group heading to print above this row (first block of the group's run only).
  groupLabel?: string;
  // Part of a navigation group — indented under its heading.
  grouped?: boolean;
  // Parent section in the client menu (block settings → "Parent section").
  isParent?: boolean;
  // Name of the parent section this block is nested under in the client menu.
  parentName?: string;
  // Level in the parent tree (0 = top level); each level indents the row further.
  depth?: number;
}

export const SectionItem = memo((props: SectionItemProps) => {
  const { block, blockForm, groupLabel, grouped, isParent, parentName, depth = 0 } = props;

  const DragHandle = SortableHandle(() => (
    <a>
      <i className="psi-handle fa fa-grip-vertical mr-2" style={{ cursor: 'move' }} />
    </a>
  ));
  return (
    <div
      className={classNames('psi-section-item', { notSection: !block.isSection, grouped, navHidden: isHiddenInNav(block), nested: !!parentName })}
      // Tree indent: 14px per level under the parent sections
      style={depth ? { paddingLeft: 16 + depth * 14 } : undefined}
      onClick={props.onClick}
    >
      {/* Navigation group heading — the blocks below it are shown indented as its members */}
      {!!groupLabel && (
        <div className="psi-group-label" title="Navigation group: one top-level section in the client proposal">
          {groupLabel}
        </div>
      )}
      <div style={{ width: '100%', padding: 5, paddingTop: 7, paddingBottom: 7 }} className="d-flex flex-row justify-content-between">
        <DragHandle />
        <span className="text-truncate" style={{ flex: 1 }}>
          {CommonService.stripHtml(block.title || R.startCase(block.variation))}
          {block.locked && <i className="fa fa-lock ml-2 text-muted"></i>}
          {/* Parent / nested markers for the client menu structure */}
          {isParent && (
            <span className="psi-parent-badge" title="Parent section: other sections can be nested under it in the client menu">
              Parent
            </span>
          )}
          {!!parentName && (
            <small className="psi-parent-name d-block text-truncate" title={`Shown under "${parentName}" in the client menu`}>
              <i className="bx bx-subdirectory-right" /> {parentName}
            </small>
          )}
        </span>
        {/* Show / hide this block in the client-facing navigation menu. The block itself stays on the
            proposal and in this list; hidden rows are dimmed and keep the eye-slash visible. */}
        {/* Pricing / signing are always shown to the client: a fixed eye, no toggle */}
        {isAlwaysVisible(block) ? (
          <a title="Always shown to the client" onClick={e => e.stopPropagation()} style={{ cursor: 'default' }}>
            <i className="psi-visibility fa fa-eye mr-2" />
          </a>
        ) : (
          <a
            title={block.hideInNav ? 'Hidden from client menu. Click to show' : 'Shown in client menu. Click to hide'}
            onClick={e => {
              e.stopPropagation();
              blockForm.patchValue({ hideInNav: !block.hideInNav });
            }}
          >
            <i className={classNames('psi-visibility fa mr-2', block.hideInNav ? 'fa-eye-slash' : 'fa-eye')} />
          </a>
        )}
        <a
          onClick={e => {
            e.stopPropagation();
            // openBlockSettingsOverlay({ form: blockForm });
            props.onSettings();
          }}
        >
          <i className="psi-setting fa fa-cog text-muted"></i>
        </a>
      </div>
    </div>
  );
});

export const SortableSectionItem = SortableElement(SectionItem);
